import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Gift, Calendar, Heart } from "lucide-react";

const BOOKING_URL = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2sLR40fV8O-hfoDyzVRQq1wTbDNf0T-8Ovy79yBcvaGSRyMCmuzMMj_fEnqs6AjhDQlKyeVbLd";

const GiftPage = () => {
  const gifts = [
    { name: "Single Support Call", description: "A 30-minute doula session perfect for a quick check-in, question, or moment of reassurance", price: "$75", link: "/services/doula-direct#30-min" },
    { name: "Extended Session", description: "A 60-minute deep-dive space to process, plan, and feel truly supported", price: "$125", link: "/services/doula-direct#60-min" },
    { name: "Monthly Support", description: "A full month of ongoing doula care because she deserves continuous support", price: "$197", link: "/services/doula-direct#monthly" },
    { name: "10-Visit Package", description: "Comprehensive, personalized support across her entire journey", price: "$1,200", link: "/services/10-visit-package" },
  ];

  return (
    <div className="min-h-screen bg-warm-white pt-24">
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-peach/50 to-clay/40 text-center">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
          <Gift className="w-12 h-12 text-teal mx-auto mb-4" />
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-plum mb-6 leading-tight">
            Give Her the Gift of Support
          </h1>
          <p className="font-body text-base md:text-lg text-plum/70 leading-relaxed max-w-2xl mx-auto">
            The most meaningful gift isn't something she unwraps it's knowing she doesn't have to do this alone. Give her access to compassionate, expert doula support during one of life's most transformative chapters.
          </p>
        </div>
      </section>

      {/* Gift Options */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 md:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-plum mb-4 text-center">
            Choose a Gift
          </h2>
          <p className="font-body text-plum/60 text-center mb-10 md:mb-14 max-w-xl mx-auto">
            Every option includes professional doula support via virtual or phone available nationwide.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {gifts.map((gift, index) => (
              <div 
                key={index} 
                data-testid={`gift-option-${index}`} 
                className="bg-white rounded-2xl p-6 shadow-lg border-2 border-peach/40 flex flex-col text-center hover:shadow-xl transition-all duration-300"
              >
                <Heart className="w-8 h-8 text-teal mx-auto mb-3" />
                <h3 className="font-heading text-xl font-medium text-plum mb-2">{gift.name}</h3>
                <p className="font-body text-sm text-plum/60 mb-4 flex-1">{gift.description}</p>
                <p className="font-heading text-3xl font-semibold text-teal mb-5">{gift.price}</p>
                <Link to={gift.link}>
                  <Button className="w-full bg-teal hover:bg-teal-dark text-white rounded-full">
                    Give This Gift
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Partners */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-peach/20 to-clay/20">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12">
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-3xl p-8 md:p-10 text-center border border-teal/20 shadow-sm">
              <h3 className="font-heading text-2xl md:text-3xl font-medium text-plum mb-4">
                For Partners & Fathers
              </h3>
              <p className="font-body text-plum/70 leading-relaxed mb-6">
                You Don't Have to Have All the Answers. You show up every day and that matters. Give her access to professional support that complements everything you already do. Because sometimes the best thing you can do is make sure she has someone in her corner.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-teal hover:bg-teal-dark text-white rounded-full px-6">
                  <Calendar className="w-4 h-4 mr-2" />
                  Gift a Session
                </Button>
              </a>
            </div>
            <div className="bg-white rounded-3xl p-8 md:p-10 text-center border border-teal/20 shadow-sm">
              <h3 className="font-heading text-2xl md:text-3xl font-medium text-plum mb-4">
                For Grandparents
              </h3>
              <p className="font-body text-plum/70 leading-relaxed mb-6">
                Give a Gift That Lasts Longer Than Flowers. Your grandchild is on the way and their mama could use support that goes beyond a baby shower. Give the gift of guidance, comfort, and confidence during pregnancy and postpartum.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-teal hover:bg-teal-dark text-white rounded-full px-6">
                  <Calendar className="w-4 h-4 mr-2" />
                  Gift a Session
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 md:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-plum mb-4">
            Not Sure Which to Choose?
          </h2>
          <p className="font-body text-plum/70 mb-8">
            Schedule a quick call and we'll help you find the perfect gift of support.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-teal hover:bg-teal-dark text-white rounded-full px-8 py-5 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Schedule a Call
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default GiftPage;
